import {Crop} from 'react-image-crop';
import {useMutation} from '@tanstack/react-query';
import React, {useState} from 'react';
import {useNotifications} from '../../../shared';
import {fundraiserImageApi} from '../api';
import {ImageType} from '../model';
// Типизация для результатов API
interface UploadPrepareItem {
  id: string;
  url: string;
}

// Типизация для параметров функций
interface CropImageParams {
  crop: Crop;
  imgRef: React.RefObject<HTMLImageElement>;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  resizeWidth?: number | null;
  resizeHeight?: number | null;
}

// Тип изображения для фандрайзера

// Типизация для результата хука при создании нового изображения
export interface PreparedImageData {
  uploadId: string;
  resizeWidth: number | null;
  resizeHeight: number | null;
  cropX: number | null;
  cropY: number | null;
  cropWidth: number | null;
  cropHeight: number | null;
}

// Типизация для результата хука
interface UseImageUploadResult {
  uploadId: string | null;
  prepareUpload: (file: File) => void;
  uploadImage: (
    crop: Crop,
    resizeWidth: number | null,
    resizeHeight: number | null,
    imgRef: React.RefObject<HTMLImageElement>,
    canvasRef: React.RefObject<HTMLCanvasElement>,
  ) => Promise<void>;
  prepareImageData: (
    crop: Crop,
    resizeWidth: number | null,
    resizeHeight: number | null,
    imgRef: React.RefObject<HTMLImageElement>,
    canvasRef: React.RefObject<HTMLCanvasElement>,
  ) => Promise<PreparedImageData | null>;
  isPreparing: boolean;
  isDirectUploading: boolean;
  isUploading: boolean;
  error: string | null;
}

/**
 * Утилита для прямой загрузки файла
 */
const uploadFileDirectly = async (file: File, uploadUrl: string): Promise<void> => {
  const buffer = await file.arrayBuffer();
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type,
    },
    body: buffer,
  });

  if (!response.ok) {
    let errorBody = 'Unknown error during direct upload.';
    try {
      errorBody = await response.text();
    } catch (e) {
      // Ignore error reading body
    }
    throw new Error(`Direct upload failed with status ${response.status}: ${errorBody}`);
  }
};

/**
 * Функция для создания обрезанного изображения (прямоугольный кроп)
 */
const cropImage = async ({
  crop,
  imgRef,
  canvasRef,
  resizeWidth = null,
  resizeHeight = null,
}: CropImageParams): Promise<File> => {
  if (!crop || !imgRef.current) {
    throw new Error('Пожалуйста, выберите область для обрезки');
  }

  // Создаем canvas для кропа изображения
  const canvas = canvasRef.current || document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Не удалось создать контекст canvas');
  }

  // Получаем размеры и рассчитываем масштаб
  const img = imgRef.current;
  const {naturalWidth, naturalHeight} = img;
  const displayWidth = img.width;
  const displayHeight = img.height;

  const scaleX = naturalWidth / displayWidth;
  const scaleY = naturalHeight / displayHeight;

  // Рассчитываем координаты в масштабе оригинала
  const cropX = Math.round(crop.x * scaleX);
  const cropY = Math.round(crop.y * scaleY);
  const cropWidth = Math.round(crop.width * scaleX);
  const cropHeight = Math.round(crop.height * scaleY);

  // Задаем размеры canvas
  const finalWidth = resizeWidth || cropWidth;
  const finalHeight = resizeHeight || cropHeight;
  canvas.width = finalWidth;
  canvas.height = finalHeight;

  // Рисуем на canvas обрезанное изображение
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, cropX, cropY, cropWidth, cropHeight, 0, 0, finalWidth, finalHeight);

  // Преобразуем canvas в Blob
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (b) resolve(b);
        else reject(new Error('Не удалось создать Blob из canvas'));
      },
      'image/jpeg',
      0.95,
    );
  });

  // Создаем файл из Blob
  return new File([blob], 'cropped-image.jpg', {type: 'image/jpeg'});
};

/**
 * Хук для работы с загрузкой изображений
 */
export const useImageUpload = (
  fundraiserId?: string,
  imageType: ImageType = ImageType.MAIN,
  isCreating: boolean = false,
): UseImageUploadResult => {
  const [uploadId, setUploadId] = useState<string | null>(null);
  const {createNotification} = useNotifications();

  // Мутация для фоновой прямой загрузки файла
  const directUploadMutation = useMutation({
    mutationFn: ({file, url}: {file: File; url: string}) => uploadFileDirectly(file, url),
    onError: () => {
      createNotification({
        name: 'direct-upload-error',
        title: 'Ошибка загрузки файла',
        theme: 'danger',
      });
      setUploadId(null);
    },
  });

  // Мутация для подготовки загрузки
  const prepareUploadMutation = useMutation({
    mutationFn: async (file: File): Promise<UploadPrepareItem> => {
      setUploadId(null);
      directUploadMutation.reset();

      const response = await fundraiserImageApi.prepareUpload([file.type]);
      if (!response.items || response.items.length === 0 || !response.items[0].url) {
        throw new Error('Prepare upload response is invalid or missing uploadUrl');
      }
      return response.items[0];
    },
    onSuccess: (data, file) => {
      setUploadId(data.id);
      directUploadMutation.mutate({file, url: data.url});
    },
    onError: (err: Error) => {
      createNotification({
        name: 'prepare-error',
        title: 'Не удалось подготовить загрузку файла',
        content: err.message,
        theme: 'danger',
      });
    },
  });

  // Функция подготовки данных для создания новой сущности
  const prepareImageData = async (
    crop: Crop,
    resizeWidth: number | null,
    resizeHeight: number | null,
    imgRef: React.RefObject<HTMLImageElement>,
    canvasRef: React.RefObject<HTMLCanvasElement>,
  ): Promise<PreparedImageData | null> => {
    if (!crop || !imgRef.current) {
      createNotification({
        name: 'crop-warning',
        title: 'Предупреждение',
        content: 'Пожалуйста, выберите область для обрезки',
        theme: 'warning',
      });
      return null;
    }

    try {
      // Используем существующую функцию для создания обрезанного изображения
      const croppedFile = await cropImage({
        crop,
        imgRef,
        canvasRef,
        resizeWidth,
        resizeHeight,
      });

      // Получаем URL для загрузки
      const response = await fundraiserImageApi.prepareUpload([croppedFile.type]);
      if (!response.items || response.items.length === 0 || !response.items[0].url) {
        throw new Error('Prepare upload response is invalid or missing uploadUrl');
      }

      // Загружаем обрезанное изображение
      await uploadFileDirectly(croppedFile, response.items[0].url);

      // Возвращаем только uploadId, так как фактическое изображение
      // уже обрезано и загружено
      return {
        uploadId: response.items[0].id,
        resizeWidth: null,
        resizeHeight: null,
        cropX: null,
        cropY: null,
        cropWidth: null,
        cropHeight: null,
      };
    } catch (error) {
      createNotification({
        name: 'crop-error',
        title: 'Ошибка обработки изображения',
        theme: 'danger',
      });
      return null;
    }
  };

  // Функция для загрузки изображения с обрезкой
  const uploadImage = async (
    crop: Crop,
    resizeWidth: number | null,
    resizeHeight: number | null,
    imgRef: React.RefObject<HTMLImageElement>,
    canvasRef: React.RefObject<HTMLCanvasElement>,
  ): Promise<void> => {
    if (!fundraiserId && !isCreating) {
      createNotification({
        name: 'entity-warning',
        title: 'Предупреждение',
        content: 'ID фандрайзера не указан',
        theme: 'warning',
      });
      return;
    }

    if (!crop || !imgRef.current) {
      createNotification({
        name: 'crop-warning',
        title: 'Предупреждение',
        content: 'Пожалуйста, выберите область для обрезки',
        theme: 'warning',
      });
      return;
    }

    try {
      // Создаем обрезанное изображение
      const croppedFile = await cropImage({
        crop,
        imgRef,
        canvasRef,
        resizeWidth,
        resizeHeight,
      });

      // Сбрасываем текущий uploadId
      setUploadId(null);
      directUploadMutation.reset();

      // Подготавливаем загрузку
      const response = await fundraiserImageApi.prepareUpload([croppedFile.type]);
      if (!response.items || response.items.length === 0 || !response.items[0].url) {
        throw new Error('Prepare upload response is invalid or missing uploadUrl');
      }

      const newUploadId = response.items[0].id;
      setUploadId(newUploadId);

      // Загружаем обрезанный файл
      await uploadFileDirectly(croppedFile, response.items[0].url);

      // Завершаем загрузку напрямую через API в зависимости от типа изображения
      if (fundraiserId) {
        const payload = {uploadId: newUploadId};

        if (imageType === ImageType.MAIN) {
          await fundraiserImageApi.uploadMainImage(fundraiserId, payload);
        } else if (imageType === ImageType.FEED) {
          await fundraiserImageApi.uploadFeedImage(fundraiserId, payload);
        } else if (imageType === ImageType.AVATAR) {
          await fundraiserImageApi.uploadAvatarImage(fundraiserId, payload);
        }
      }

      setUploadId(null);
    } catch (error) {
      createNotification({
        name: 'crop-error',
        title: 'Ошибка сервера',
        theme: 'danger',
      });
    }
  };

  const error = prepareUploadMutation.error?.message || directUploadMutation.error?.message || null;

  return {
    uploadId,
    prepareUpload: prepareUploadMutation.mutate,
    uploadImage,
    prepareImageData,
    isPreparing: prepareUploadMutation.isPending,
    isDirectUploading: directUploadMutation.isPending,
    isUploading: false,
    error,
  };
};
